import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CalendarDays, Store, Eye, MoreHorizontal } from "lucide-react";
import { cn } from "@/lib/utils";
import { formatCurrency } from "@/lib/currency";
import { ReceiptDetails } from "./ReceiptDetails";
import { useTranslation } from "react-i18next";

interface ReceiptItem {
  name: string;
  amount: number;
  category: "food" | "household" | "health" | "personal" | "other";
  quantity: number;
  price: number;
}

interface Receipt {
  id: string; // receipts.id is a UUID
  date: string;
  store: string;
  total: number;
  items: ReceiptItem[];
  imageUrl: string;
  address?: string;
  currency?: string;
}

interface ReceiptCardProps {
  receipt: Receipt;
}

const categoryColors = {
  food: "bg-category-food/20 text-category-food border-category-food/30",
  household: "bg-category-household/20 text-category-household border-category-household/30",
  health: "bg-category-health/20 text-category-health border-category-health/30",
  personal: "bg-category-personal/20 text-category-personal border-category-personal/30",
  other: "bg-category-other/20 text-category-other border-category-other/30",
};

const categoryIcons = {
  food: "🍎",
  household: "🏠",
  health: "💊",
  personal: "🧴",
  other: "📦",
};

export const ReceiptCard = ({ receipt }: ReceiptCardProps) => {
  const { t } = useTranslation();
  const [showDetails, setShowDetails] = useState(false);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getCategorySummary = () => {
    const summary = receipt.items.reduce((acc, item) => {
      acc[item.category] = (acc[item.category] || 0) + item.amount;
      return acc;
    }, {} as Record<string, number>);
    
    return Object.entries(summary)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 3);
  };

  const topCategories = getCategorySummary();

  return (
    <Card className="shadow-custom-md border-0 hover:shadow-custom-lg transition-all duration-300 group">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="space-y-1">
            <CardTitle className="text-lg flex items-center gap-2">
              <Store className="h-4 w-4 text-primary" />
              {receipt.store}
            </CardTitle>
            <div className="flex items-center text-sm text-muted-foreground">
              <CalendarDays className="h-3 w-3 mr-1" />
              {formatDate(receipt.date)}
            </div>
          </div>
          <Button variant="ghost" size="sm" className="opacity-0 group-hover:opacity-100 transition-opacity">
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        <div className="flex justify-between items-center">
          <span className="text-2xl font-bold text-primary">
            {formatCurrency(receipt.total, receipt.currency || 'USD')}
          </span>
          <span className="text-sm text-muted-foreground">
            {t('receiptCard.items', { count: receipt.items.length })}
          </span>
        </div>

        <div className="space-y-2">
          <div className="text-sm font-medium">{t('receiptCard.topCategories')}</div>
          <div className="flex flex-wrap gap-2">
            {topCategories.map(([category, amount]) => (
              <Badge 
                key={category}
                variant="outline" 
                className={cn(
                  "text-xs",
                  categoryColors[category as keyof typeof categoryColors]
                )}
              >
                {categoryIcons[category as keyof typeof categoryIcons]} {formatCurrency(amount, receipt.currency || 'USD')}
              </Badge>
            ))}
          </div>
        </div>
        
        <div className="pt-2 border-t">
          <Button
            variant="outline"
            size="sm"
            className="w-full hover:bg-primary hover:text-primary-foreground transition-colors"
            onClick={() => setShowDetails(true)}
          >
            <Eye className="h-4 w-4 mr-2" />
            {t('receiptCard.viewDetails')}
          </Button>
        </div>
      </CardContent>

      <ReceiptDetails 
        receipt={receipt}
        open={showDetails}
        onOpenChange={setShowDetails}
      />
    </Card>
  );
};